/**
 * Marker Type Configuration
 * 
 * Unified configuration system for map marker types.
 * Each marker type defines:
 * - Display label
 * - Icon/avatar behavior
 * - Which metadata fields to display
 * - Which standard fields to show
 * - Type-specific behavior
 */

/**
 * Standard field keys that can be displayed in popup
 */
export const STANDARD_FIELDS = Object.freeze({
    AUTHOR: 'author',
    VISIBILITY: 'visibility',
    CREATED: 'created',
});

/**
 * Common metadata field keys
 */
export const METADATA_FIELDS = Object.freeze({
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    DESCRIPTION: 'description',
    CITY: 'city',
    STATE_OR_REGION: 'stateOrRegion',
    COUNTRY: 'country',
    LOCATION: 'location',
    ADDRESS: 'address',
    PHONE: 'phone',
    WEBSITE: 'website',
    EMAIL: 'email',
    PREFERRED_STYLES: 'preferredStyles',
    INSTRUMENT_EXPERIENCE: 'instrumentExperience',
    INFLUENCES: 'influences',
    SEEKING: 'seeking',
    COMMITMENT: 'commitment',
    ABOUT: 'about',
    EQUIPMENT: 'equipment',
    TIMEZONE: 'timezone',
});

/**
 * Icon display modes
 */
export const ICON_MODES = Object.freeze({
    AVATAR: 'avatar',           // Show user avatar (for Artist)
    MARKER_ICON: 'markerIcon',  // Show marker icon with layer color
    NONE: 'none',               // No icon
});

/**
 * Marker type configuration
 * 
 * @typedef {Object} MarkerTypeConfig
 * @property {string} label - Display label for the marker type
 * @property {string} iconMode - Icon display mode (ICON_MODES)
 * @property {boolean} showAuthor - Whether to show author field
 * @property {boolean} showVisibility - Whether to show visibility field
 * @property {boolean} showCreated - Whether to show created date field
 * @property {string[]} priorityMetadataFields - Metadata fields to show first (in order)
 * @property {boolean} allowEdit - Whether this marker type can be edited
 * @property {Function} extractAvatar - Function to extract avatar data from marker
 */

/**
 * Extract avatar data for Artist type
 * @param {Object} marker - Marker data
 * @param {Object} metadata - Marker metadata
 * @param {string} author - Author name
 * @param {string} authorUsername - Author username
 * @returns {Object|null} Avatar data or null
 */
function extractArtistAvatar(marker, metadata, author, authorUsername) {
    const avatarUrl = typeof metadata.avatarUrl === 'string' ? metadata.avatarUrl.trim() : undefined;
    
    if (!marker.createdById && !authorUsername && !author && !avatarUrl) {
        return null;
    }
    
    return {
        userId: marker.createdById,
        username: authorUsername || author,
        avatarUrl,
    };
}

function readTextValue(value) {
    if (typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    return trimmed || '';
}

/**
 * Checks whether avatar payload has any usable identity/image data.
 * @param {Object|null|undefined} avatar
 * @returns {boolean}
 */
export function hasMarkerAvatarData(avatar) {
    if (!avatar || typeof avatar !== 'object' || Array.isArray(avatar)) {
        return false;
    }
    const userId = avatar.userId;
    const normalizedUserId = typeof userId === 'string' ? userId.trim() : userId;
    return Boolean(
        normalizedUserId
        || readTextValue(avatar.username)
        || readTextValue(avatar.avatarUrl),
    );
}

function readAvatarUrlFromObject(candidate) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return '';
    }
    const avatarUrl = readTextValue(candidate.avatarUrl);
    if (avatarUrl) {
        return avatarUrl;
    }
    const url = readTextValue(candidate.url);
    if (url) {
        return url;
    }
    const imageUrl = readTextValue(candidate.imageUrl);
    if (imageUrl) {
        return imageUrl;
    }
    return '';
}

function resolveBandAvatarUrl(marker, metadata) {
    const directKeys = [
        marker?.avatarUrl,
        marker?.imageUrl,
        marker?.bandImageUrl,
        marker?.profileImageUrl,
        metadata?.avatarUrl,
        metadata?.imageUrl,
        metadata?.bandImageUrl,
        metadata?.profileImageUrl,
    ];
    for (const value of directKeys) {
        const normalized = readTextValue(value);
        if (normalized) {
            return normalized;
        }
    }

    const nestedSources = [
        marker?.avatar,
        marker?.image,
        marker?.profileImage,
        marker?.images,
        metadata?.avatar,
        metadata?.image,
        metadata?.profileImage,
        metadata?.images,
    ];

    for (const source of nestedSources) {
        const nestedUrl = readAvatarUrlFromObject(source);
        if (nestedUrl) {
            return nestedUrl;
        }
        const avatarNode = source?.avatar;
        const nestedAvatarUrl = readAvatarUrlFromObject(avatarNode);
        if (nestedAvatarUrl) {
            return nestedAvatarUrl;
        }
    }

    return '';
}

function extractBandAvatar(marker, metadata) {
    const avatarUrl = resolveBandAvatarUrl(marker, metadata);

    if (!avatarUrl) {
        return null;
    }

    return {
        userId: null,
        avatarUrl,
    };
}

/**
 * Default avatar extractor (returns null)
 */
function extractNoAvatar() {
    return null;
}

/**
 * Marker type configurations
 * 
 * To add a new marker type:
 * 1. Add the type constant above
 * 2. Add configuration entry here with all required properties
 * 3. The configuration will automatically be used throughout the application
 */
export const MARKER_TYPE_CONFIG = Object.freeze({
    ARTIST: {
        label: 'Artist',
        iconMode: ICON_MODES.AVATAR,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.DESCRIPTION,
        ],
        allowEdit: false,
        extractAvatar: extractArtistAvatar,
    },
    BAND: {
        label: 'Band',
        iconMode: ICON_MODES.MARKER_ICON,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.DESCRIPTION,
            METADATA_FIELDS.WEBSITE,
        ],
        allowEdit: false,
        extractAvatar: extractBandAvatar,
    },
    EVENT: {
        label: 'Event',
        iconMode: ICON_MODES.MARKER_ICON,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.START_DATE,
            METADATA_FIELDS.END_DATE,
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.DESCRIPTION,
        ],
        allowEdit: true,
        extractAvatar: extractNoAvatar,
    },
    SESSION: {
        label: 'Session',
        iconMode: ICON_MODES.MARKER_ICON,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.START_DATE,
            METADATA_FIELDS.END_DATE,
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.DESCRIPTION,
        ],
        allowEdit: true,
        extractAvatar: extractNoAvatar,
    },
    VENUE: {
        label: 'Venue',
        iconMode: ICON_MODES.MARKER_ICON,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.ADDRESS,
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.PHONE,
            METADATA_FIELDS.WEBSITE,
            METADATA_FIELDS.DESCRIPTION,
        ],
        allowEdit: true,
        extractAvatar: extractNoAvatar,
    },
    POINT: {
        label: 'Point of interest',
        iconMode: ICON_MODES.MARKER_ICON,
        showAuthor: true,
        showVisibility: true,
        showCreated: true,
        priorityMetadataFields: [
            METADATA_FIELDS.CITY,
            METADATA_FIELDS.DESCRIPTION,
        ],
        allowEdit: true,
        extractAvatar: extractNoAvatar,
    },
});

/**
 * Get marker type configuration
 * @param {string} type - Marker type (normalized to uppercase)
 * @returns {MarkerTypeConfig} Configuration for the marker type
 */
export function getMarkerTypeConfig(type) {
    if (!type || typeof type !== 'string') {
        return MARKER_TYPE_CONFIG.POINT;
    }
    
    const normalizedType = type.trim().toUpperCase();
    return MARKER_TYPE_CONFIG[normalizedType] || MARKER_TYPE_CONFIG.POINT;
}

/**
 * Normalize marker type value
 * @param {*} value - Marker type value
 * @returns {string} Normalized type (uppercase)
 */
export function normalizeMarkerType(value) {
    if (value === undefined || value === null) {
        return '';
    }
    return String(value).trim().toUpperCase();
}
